import { bool, node, number, oneOf, string } from "prop-types";
import { forwardRef, useMemo } from "react";

import styled from "styled-components";
import {
  backgroundColor,
  borderRadius,
  color,
  fontSize,
  negitiveMargin,
  padding,
  themeSizes,
  themeVariants,
} from "theme";
import { colorVariants, virtues, widthBySize } from "theme/constants";

import FnrLoadingBricks2 from "UI/FnrLoadingBricks2";
import FnrSlides from "UI/FnrSlides";

// TODO: This load state is wip and work well for the md size. Let's get it Looking good for ALL sizes.
const Sizer = styled("div")`
  height: calc(${({ $size, theme: { fontSizes } }) => fontSizes[$size]} + 6px);
  width: ${({ $size }) => widthBySize[$size]}px;
`;

const SDiv = styled("div")`
  ${backgroundColor}
  ${color}
  ${fontSize}
  ${borderRadius}
  ${negitiveMargin}
  ${padding}
  ${({ $yCorrection }) =>
    $yCorrection ? `transform: translateY(${$yCorrection}px);` : ""}
  display: inline-block;
  transition: color 300ms, font-size 300ms, min-width 300ms, padding 300ms,
    background-color 300ms, top 300ms, left 300ms; /* top and left need to be here for things such as FnrIconBubbles */
`;

const slidesConfig = [
  {
    key: "icon",
    value: ({
      size,
      colorVariant,
      colorString,
      backgroundColorString,
      children,
      iconRef,
      yCorrection,
      // noBackgroundColor,
      // noPadding,
      ...props
    }) => (
      <SDiv
        $backgroundColor={backgroundColorString}
        $size={size}
        $color={colorString}
        $colorVariant={colorVariant}
        $variant={themeVariants.icon}
        role="presentation"
        ref={iconRef}
        $yCorrection={yCorrection}
        {...props}
      >
        {children}
      </SDiv>
    ),
  },
  {
    key: "loading",
    value: ({ colorString, size }) => (
      <Sizer $size={size}>
        <FnrLoadingBricks2
          loading
          size={widthBySize[size] * 0.75}
          color={colorString}
          alignTop
        />
      </Sizer>
    ),
  },
  {
    key: "hide",
    value: () => null,
  },
];

const FnrIconContainer = forwardRef(
  /**
   *
   * @param {object} param0
   * @param {string} param0.color - color from theme file, or a hexcode
   * @param {string} param0.backgroundColor - color from theme file, or a hexcode
   * @param {"xs"|"sm"|"md"|"lg"|"xl"} param0.size - size of the icon using font sizes from theme file
   * @param {"default"|"dark"} param0.colorVariant
   * @param {virtue} param0.virtue - define icon color using a governing virtue
   * @param {boolean} param0.isLoading
   * @param {boolean} param0.show
   * @param {number} param0.yCorrection - some icons in certain sizes need tweak on the y axis. Use this to get it perfectly aligned.
   * @param {boolean} param0.noAnimation - disables all animation as this can sometimes make things glitchy.
   * @returns
   */
  (
    {
      size,
      colorVariant,
      color: colorString,
      backgroundColor: backgroundColorString,
      virtue,
      isLoading,
      show = true,
      yCorrection,
      noAnimation,
      ...rest
    },
    ref
  ) => {
    const componentKey = useMemo(() => {
      if (!show) return "hide";
      if (isLoading) return "loading";
      return "icon";
    }, [isLoading, show]);

    if (noAnimation)
      return (
        <SDiv
          $backgroundColor={backgroundColorString}
          $size={size}
          $color={colorString}
          $colorVariant={colorVariant}
          $variant={themeVariants.icon}
          role="presentation"
          ref={ref}
          $yCorrection={yCorrection}
          {...rest}
        />
      );

    return (
      <FnrSlides
        activeKey={componentKey}
        components={slidesConfig}
        clip={false}
        colorVariant={colorVariant}
        size={size}
        colorString={colorString}
        backgroundColorString={backgroundColorString}
        $virtue={virtue}
        iconRef={ref}
        variant="zoom"
        yCorrection={yCorrection}
        {...rest}
      />
    );
  }
);

FnrIconContainer.propTypes = {
  size: oneOf(Object.values(themeSizes)),
  colorVariant: oneOf(Object.values(colorVariants)),
  children: node,
  color: string,
  backgroundColor: string,
  virtue: oneOf(Object.values(virtues)),
  isLoading: bool,
  show: bool,
  yCorrection: number,
  noAnimation: bool,
};

export default FnrIconContainer;
